import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchAppointmentById } from "../../store/thunks/appointmentsThunk";
import {
  fetchInvoicePaymentsByAppointmentId,
  fetchPaymentsByInvoice,
} from "../../store/thunks/paymentsThunk";
import { setSelectedAppointment } from "../../store/slices/appointmentsSlice";
import InvoicePaymentModal from "../../modals/InvoicePaymentModal"; // Import the modal
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  PDFViewer,
  Image,
  pdf,
} from "@react-pdf/renderer";
// Logo path for @react-pdf/renderer - must use public folder for PDF generation compatibility
import InvoiceModal from "./InvoiceModal";
import RefundDialog from "./RefundDialog";
import PaymentRefundModal from "./PaymentRefundModal";
import { saveAndEmailInvoice, refundInvoice, fetchInvoicesByAppointmentId } from "../../store/thunks/invoiceThunk";
import { refundPayment } from "../../store/thunks/paymentsThunk";
import { setCurrentInvoice } from "../../store/slices/invoiceSlice";
import { toast } from 'react-toastify';
// Logo path - uses local PNG file for PDF renderer compatibility
const logo = `${process.env.PUBLIC_URL}/listenhear_logo.png`;

let totalPaid = 0;
let totalDepositUsed = 0;
let totalAmount = 0; // For the invoice total amount (e.g., $150 in your example)

const InvoiceGenerator = ({ initialViewMode = false, data = null }) => {
  const dispatch = useDispatch();

  const selectedAppointment = useSelector(
    (state) => state.appointments.selectedAppointment
  );
  const currentUser = useSelector((state) => state.auth.user);

  // Check if user has refund privileges (owner or admin only)
  const hasRefundPermission = currentUser && currentUser.role && Array.isArray(currentUser.role) && (
    currentUser.role.includes('owner') || 
    currentUser.role.includes('admin')
  );
  const [viewMode, setViewMode] = useState(initialViewMode);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isSplitInvoiceModalOpen, setIsSplitInvoiceModalOpen] = useState(false);
  const [isEditInvoiceModalOpen, setIsEditInvoiceModalOpen] = useState(false);
  const [isRefundDialogOpen, setIsRefundDialogOpen] = useState(false);
  const [isPaymentRefundModalOpen, setIsPaymentRefundModalOpen] = useState(false);
  const [selectedPaymentForRefund, setSelectedPaymentForRefund] = useState(null);

  const invoiceData = useSelector((state) => state.invoices.currentInvoice);
  const paymentData = useSelector(
    (state) => state.payments.appointmentPayments
  );

  const onEditInvoice = () => {
    setIsEditInvoiceModalOpen(true);
  };

  const onSplitInvoice = () => {
    setIsSplitInvoiceModalOpen(true);
  };

  const handleRefundInvoice = async (reason) => {
    try {
      console.log(`[Invoice Generator] Refunding invoice ${invoiceData.invoiceId} with reason: ${reason}`);
      
      await dispatch(refundInvoice({
        invoiceId: invoiceData._id,
        reason: reason
      })).unwrap();

      toast.success("Refund processed successfully");
      
      // Refresh the appointment data to reflect the updated status
      if (invoiceData.appointmentId) {
        dispatch(fetchAppointmentById(invoiceData.appointmentId));
      }
      
    } catch (error) {
      console.error(`[Invoice Generator] Error processing refund:`, error);
      
      // Handle specific error cases
      if (error.error && error.error.includes('privileges')) {
        toast.error("You don't have permission to refund invoices. Only owners and administrators can perform refunds.");
      } else {
        toast.error(error.error || "Failed to process refund. Please try again.");
      }
    }
  };

  const handleOpenPaymentModal = async () => {
    try {
      console.log(`[Invoice Generator] Opening payment modal for invoice ${invoiceData.invoiceId}`);
      console.log(`[Invoice Generator] Invoice appointment ID: ${invoiceData.appointmentId}`);
      
      // Fetch invoices by appointment ID to populate appointmentInvoices in Redux
      if (invoiceData.appointmentId) {
        await dispatch(fetchInvoicesByAppointmentId(invoiceData.appointmentId));
        console.log(`[Invoice Generator] Successfully fetched appointment invoices for appointment ${invoiceData.appointmentId}`);
      }
      
      setIsPaymentModalOpen(true);
    } catch (error) {
      console.error(`[Invoice Generator] Error opening payment modal:`, error);
      toast.error("Failed to load payment modal. Please try again.");
    }
  };

  const handlePaymentRefund = (payment) => {
    if (invoiceData?.status !== 'refunded') {
      toast.error("The invoice must be refunded before you can refund individual payments");
      return;
    }
    
    setSelectedPaymentForRefund(payment);
    setIsPaymentRefundModalOpen(true);
  };

  const handlePaymentRefundConfirm = async (refundData) => {
    try {
      console.log(`[Invoice Generator] Processing payment refund:`, {
        payment: selectedPaymentForRefund,
        refundData
      });
      
      await dispatch(refundPayment({
        paymentId: selectedPaymentForRefund._id,
        refundType: refundData.refundType,
        paymentMethod: refundData.paymentMethod,
        reason: refundData.reason
      })).unwrap();
      
      if (refundData.refundType === 'refund') {
        toast.success("Payment refund processed successfully");
      } else {
        toast.success("Payment converted to deposit successfully");
      }
      
      // Refresh payment data for the current appointment
      if (invoiceData?.appointmentId) {
        await dispatch(fetchInvoicePaymentsByAppointmentId(invoiceData.appointmentId));
      }
      
    } catch (error) {
      console.error(`[Invoice Generator] Error processing payment refund:`, error);
      
      // Handle specific error cases
      if (error.error && error.error.includes('must be refunded')) {
        toast.error("The invoice must be refunded before you can refund individual payments");
      } else if (error.error && error.error.includes('privileges')) {
        toast.error("You don't have permission to refund payments. Only owners and administrators can perform refunds.");
      } else {
        toast.error(error.error || "Failed to process payment refund");
      }
    }
  };

  const saveAndEmailPDF = async () => {
    try {
      // Generate PDF blob
      const blob = await pdf(
        <MyDocument
          selectedAppointment={selectedAppointment}
          invoiceData={invoiceData}
          paymentData={paymentData}
        />
      ).toBlob();

      // Convert blob to base64
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = async () => {
        const base64data = reader.result.split(",")[1];

        const result = await dispatch(
          saveAndEmailInvoice({
            recipientEmail: selectedAppointment.client.email,
            invoicePdf: base64data,
            invoiceNumber: invoiceData.invoiceId,
            clientName:
              selectedAppointment.client.firstName +
              " " +
              selectedAppointment.client.lastName,
            amount: invoiceData.amount,
            appointmentId: selectedAppointment._id,
            userId: currentUser._id,
          })
        ).unwrap();

        toast.success("Invoice has been emailed to client");
      };
    } catch (error) {
      console.error("Error sending invoice:", error);
      toast.error("Failed to send invoice. Please try again.");
    }
  };

  useEffect(() => {
    dispatch(setCurrentInvoice(invoiceData));
  }, [invoiceData]);

  useEffect(() => {
    if (invoiceData?.appointmentId) {
      // Dispatch fetchAppointmentById and set selected appointment
      dispatch(fetchAppointmentById(invoiceData.appointmentId))
        .unwrap()
        .then((appointment) => {
          dispatch(setSelectedAppointment(appointment));
        })
        .catch((error) => {
          console.error(
            `Failed to fetch appointment with ID ${invoiceData.appointmentId}:`,
            error
          );
        });

      // Dispatch fetchPaymentsByInvoice with invoiceData._id
      dispatch(fetchInvoicePaymentsByAppointmentId(invoiceData.appointmentId))
        .unwrap()
        .then((payments) => {
          console.log(
            "hELLO FETCHING PAYMENT BY INBVOICE Fetched payments for invoice:",
            payments
          );
        })
        .catch((error) => {
          console.error(
            `Failed to fetch payments for invoice with ID ${invoiceData._id}:`,
            error
          );
        });
    }
  }, [dispatch, invoiceData]);

  console.log("invoice generator opened");
  console.log("Invoice data", invoiceData);
  console.log("Payment data", paymentData);
  console.log("Selected appointment (fetched)", selectedAppointment);
  console.log(`[Invoice Generator] User refund permission: ${hasRefundPermission}, User role: ${currentUser?.role}`);
  
  // Check refund button visibility conditions
  const canRefund = hasRefundPermission && 
    (invoiceData?.status === 'sent' || 
     invoiceData?.status === 'paid' || 
     invoiceData?.status === 'partially paid');
  console.log(`[Invoice Generator] Refund button visible: ${canRefund}, Invoice status: ${invoiceData?.status}`);

  return (
    <div className="bg-white rounded-sm shadow-sm border border-gray-200 p-6 space-y-6">
      {/* Status Information */}
      <div className="bg-gray-50 rounded-sm border border-gray-200 p-4">
        <div className="flex items-center space-x-3">
          <div className="h-10 w-10 rounded-full bg-blue-600 flex items-center justify-center">
            <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Current Status
            </h3>
            <p className="text-sm text-gray-600 mt-0.5">
              {invoiceData
                ? invoiceData.status === "created"
                  ? "Invoiced"
                  : invoiceData.status === "refunded"
                  ? `Refunded - ${invoiceData.invoiceId}`
                  : invoiceData.status === "void"
                  ? `VOIDED - ${invoiceData.invoiceId}`
                  : `${invoiceData.status} - ${invoiceData.invoiceId}`
                : "No Invoice Data"}
            </p>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className={`grid grid-cols-1 md:grid-cols-2 ${hasRefundPermission ? 'lg:grid-cols-6' : 'lg:grid-cols-5'} gap-3`}>
        <button
          onClick={handleOpenPaymentModal}
          className="inline-flex items-center justify-center px-4 py-2.5 bg-green-600 text-white text-sm font-semibold rounded-sm shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-600 transition-colors">
          <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
          </svg>
          Collect Payment
        </button>
        
        <button
          onClick={saveAndEmailPDF}
          className="inline-flex items-center justify-center px-4 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-sm shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600 transition-colors">
          <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 7.89a2 2 0 002.83 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          Email to Client
        </button>
        
        <button
          onClick={() => {
            window.location.href = `/payment/checkout/${invoiceData._id}`;
          }}
          className="inline-flex items-center justify-center px-4 py-2.5 bg-indigo-600 text-white text-sm font-semibold rounded-sm shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-600 transition-colors">
          <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
          Email Payment Link
        </button>
        
        <button
          onClick={() => onEditInvoice(invoiceData)}
          className="inline-flex items-center justify-center px-4 py-2.5 bg-gray-600 text-white text-sm font-semibold rounded-sm shadow-sm hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-600 transition-colors">
          <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
          Edit Invoice
        </button>
        
        <button
          onClick={() => onSplitInvoice(invoiceData)}
          className="inline-flex items-center justify-center px-4 py-2.5 bg-orange-600 text-white text-sm font-semibold rounded-sm shadow-sm hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-600 transition-colors">
          <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
          </svg>
          Convert to Split
        </button>
        
        {hasRefundPermission && 
         (invoiceData?.status === 'sent' || 
          invoiceData?.status === 'paid' || 
          invoiceData?.status === 'partially paid') && (
          <button
            onClick={() => setIsRefundDialogOpen(true)}
            className="inline-flex items-center justify-center px-4 py-2.5 bg-red-600 text-white text-sm font-semibold rounded-sm shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-600 transition-colors">
            <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 15v-1a4 4 0 00-4-4H8m0 0l3 3m-3-3l3-3m9 14V5a2 2 0 00-2-2H6a2 2 0 00-2 2v16l4-2 4 2 4-2 4 2z" />
            </svg>
            Refund Invoice
          </button>
        )}
      </div>

      {/* PDF Viewer */}
      <div className="border border-gray-200 rounded-sm overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
          <h4 className="text-sm font-medium text-gray-900">Invoice Preview</h4>
        </div>
        <div className="p-4 bg-white">
          <PDFViewer
            style={{
              width: "100%",
              height: "600px",
              border: "none",
            }}>
            <MyDocument
              selectedAppointment={selectedAppointment}
              invoiceData={invoiceData}
              paymentData={paymentData}
            />
          </PDFViewer>
        </div>
      </div>

      {/* Payments Section - Only show for refunded invoices */}
      {invoiceData?.status === 'refunded' && hasRefundPermission && paymentData && paymentData.length > 0 && (
        <div className="border border-gray-200 rounded-sm overflow-hidden">
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
            <h4 className="text-sm font-medium text-gray-900">Invoice Payments</h4>
            <p className="text-xs text-gray-600 mt-1">
              Individual payments can be refunded or converted to deposits for refunded invoices
            </p>
          </div>
          <div className="p-4 bg-white">
            <div className="space-y-3">
              {paymentData
                .filter(paymentItem => {
                  const payment = paymentItem.payment || paymentItem;
                  if (!payment || payment.deposit) return false; // Skip deposit payments (but include converted payments for display)
                  
                  // Check if this payment belongs to this specific invoice (handle split invoices)
                  const isForThisInvoice = (() => {
                    // For direct payment.invoiceId match
                    if (payment.invoiceId?.invoiceId === invoiceData.invoiceId) {
                      return true;
                    }
                    
                    // For array of invoices, check exact match (important for split invoices)
                    if (Array.isArray(payment.invoiceId)) {
                      return payment.invoiceId.some(inv => inv.invoiceId === invoiceData.invoiceId);
                    }
                    
                    // For split invoices, ensure we only show payments specifically allocated to this part
                    // Check if payment amount was specifically allocated to this invoice part
                    if (payment.invoiceAllocations && Array.isArray(payment.invoiceAllocations)) {
                      return payment.invoiceAllocations.some(allocation => 
                        allocation.invoiceId === invoiceData.invoiceId
                      );
                    }
                    
                    return false;
                  })();
                  
                  return isForThisInvoice;
                })
                .map((paymentItem, index) => {
                  const payment = paymentItem.payment || paymentItem;
                  const isRefund = payment.isRefund;
                  const amount = payment.amount || 0;
                  
                  return (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            isRefund ? 'bg-orange-100 text-orange-800' : 
                            payment.convertedToDeposit ? 'bg-blue-100 text-blue-800' : 
                            'bg-green-100 text-green-800'
                          }`}>
                            {isRefund ? 'Refund' : payment.convertedToDeposit ? 'Converted to Deposit' : 'Payment'}
                          </span>
                          <span className="text-sm font-medium text-gray-900">
                            {isRefund ? `-$${Math.abs(amount).toFixed(2)}` : `$${Math.abs(amount).toFixed(2)}`}
                          </span>
                          <span className="text-xs text-gray-500">
                            {payment.paymentMethod?.charAt(0).toUpperCase() + payment.paymentMethod?.slice(1)}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {isRefund && payment.refundedAt 
                            ? `Refunded: ${new Date(payment.refundedAt).toLocaleDateString("en-US", {
                                weekday: 'short',
                                year: "numeric",
                                month: "short", 
                                day: "numeric",
                              })}`
                            : new Date(payment.paymentDate).toLocaleDateString("en-US", {
                                weekday: 'short',
                                year: "numeric",
                                month: "short", 
                                day: "numeric",
                              })
                          }
                          {isRefund && payment.refundReason && (
                            <span className="ml-2">• {payment.refundReason}</span>
                          )}
                        </div>
                      </div>
                      
                      {/* Only show refund button for non-refund payments that weren't converted to deposits */}
                      {!isRefund && !payment.convertedToDeposit && (
                        <button
                          onClick={() => handlePaymentRefund(payment)}
                          className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-orange-700 bg-orange-100 border border-orange-200 rounded hover:bg-orange-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors"
                        >
                          <svg className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 15v-1a4 4 0 00-4-4H8m0 0l3 3m-3-3l3-3m9 14V5a2 2 0 00-2-2H6a2 2 0 00-2 2v16l4-2 4 2 4-2 4 2z" />
                          </svg>
                          Refund Payment
                        </button>
                      )}
                    </div>
                  );
                })
              }
            </div>
          </div>
        </div>
      )}

      {/* Invoice Payment Modal */}
      {isPaymentModalOpen && (
        <InvoicePaymentModal
          // invoice={invoiceData}
          onClose={async () => {
            setIsPaymentModalOpen(false); // Close the modal
          }}
        />
      )}

      {isSplitInvoiceModalOpen && (
        <InvoiceModal
          appointment={selectedAppointment}
          convertInvoice={invoiceData} // Pass convertInvoice to modal
          onClose={() => setIsSplitInvoiceModalOpen(false)}
        />
      )}

      {isEditInvoiceModalOpen && (
        <InvoiceModal
          appointment={selectedAppointment}
          editInvoice={invoiceData}
          onClose={() => setIsEditInvoiceModalOpen(false)}
        />
      )}

      {/* Refund Dialog */}
      {isRefundDialogOpen && (
        <RefundDialog
          isOpen={isRefundDialogOpen}
          onClose={() => setIsRefundDialogOpen(false)}
          onConfirm={handleRefundInvoice}
          invoiceId={invoiceData?.invoiceId}
          amount={invoiceData?.amount}
        />
      )}

      {/* Payment Refund Modal */}
      {isPaymentRefundModalOpen && selectedPaymentForRefund && (
        <PaymentRefundModal
          isOpen={isPaymentRefundModalOpen}
          onClose={() => {
            setIsPaymentRefundModalOpen(false);
            setSelectedPaymentForRefund(null);
          }}
          onConfirm={handlePaymentRefundConfirm}
          payment={selectedPaymentForRefund}
          invoice={invoiceData}
        />
      )}
    </div>
  );
};

export default InvoiceGenerator;

const MyDocument = ({ selectedAppointment, invoiceData, paymentData }) => {
  console.log("My document selectedAppointment", selectedAppointment);
  console.log("My document invoiceData", invoiceData);
  console.log("My document paymentData", paymentData);
  // const clinic = selectedAppointment?.clinic || {};
  // const clinician = selectedAppointment?.clinician || {};
  // const client = selectedAppointment?.client || {};
  // const address = selectedAppointment?.client?.address || {};
  const clinic = invoiceData?.clinic || {};
  const clinician = invoiceData?.clinician || {};
  const client = invoiceData?.client || {};
  const insurer = invoiceData?.billTo || {};
  const billToType = invoiceData?.billToType || "client";
  const address = invoiceData?.client?.address || {};
  const services = invoiceData?.services || [];
  const parentInfo = selectedAppointment?.splitBilling?.parentInfo || {};
  const paymentInfo = Array.isArray(paymentData) ? paymentData : [];

  console.log("Payment Info", paymentInfo);

  const calculateSubtotal = (services, isRefunded = false) => {
    if (!isRefunded) {
      // Normal invoice - just sum up services
      return services.reduce((acc, service) => acc + service.cost * service.quantity, 0);
    } else {
      // Refunded invoice - show original services + refund offset = 0
      return 0; // Net total after refund offset
    }
  };

  const calculateTax = (services, isRefunded = false) => {
    if (!isRefunded) {
      // Normal invoice - calculate tax on services
      return services.reduce((acc, service) => {
        if (service.isTaxable && service.taxRate && service.taxRate > 0) {
          return acc + service.cost * service.quantity * service.taxRate;
        }
        return acc;
      }, 0);
    } else {
      // Refunded invoice - net tax after refund offset = 0
      return 0;
    }
  };

  const isRefunded = invoiceData.status === 'refunded';
  const subtotal = calculateSubtotal(services, isRefunded);
  const tax = calculateTax(services, isRefunded);
  const total = subtotal + tax;

  const totalPayments = paymentInfo.reduce((sum, paymentItem) => {
    const payment = paymentItem.payment || paymentItem;
    if (!payment) return sum;

    // Check if this payment belongs to this specific invoice (handle split invoices)
    const isForThisInvoice = (() => {
      // For direct payment.invoiceId match
      if (payment.invoiceId?.invoiceId === invoiceData.invoiceId) {
        return true;
      }
      
      // For array of invoices, check exact match (important for split invoices)
      if (Array.isArray(payment.invoiceId)) {
        return payment.invoiceId.some(inv => inv.invoiceId === invoiceData.invoiceId);
      }
      
      // For split invoices, ensure we only show payments specifically allocated to this part
      // Check if payment amount was specifically allocated to this invoice part
      if (payment.invoiceAllocations && Array.isArray(payment.invoiceAllocations)) {
        return payment.invoiceAllocations.some(allocation => 
          allocation.invoiceId === invoiceData.invoiceId
        );
      }
      
      return false;
    })();

    if (!isForThisInvoice) return sum;

    let paymentSum = sum;
    // Only count direct payment if not a deposit
    if (!payment.deposit) {
      if (payment.isRefund && payment.refundedAt) {
        // Only treat as refund if money was actually returned (has refundedAt date)
        // The refund effectively cancels out the original payment
        paymentSum += 0;
      } else if (payment.convertedToDeposit) {
        // Don't count payments that were converted to deposits (prevents double-counting)
        paymentSum += 0;
      } else {
        // Normal payments (including payments for refunded invoices) reduce the balance
        paymentSum += Math.abs(payment.amount || 0);
      }
    }
    // Only count deposit usage for this invoice
    if (payment.deposit && Array.isArray(payment.depositUsage)) {
      paymentSum += payment.depositUsage
        .filter(dep => dep.invoiceId === invoiceData._id || dep.invoiceNumber === invoiceData.invoiceId)
        .reduce((depSum, dep) => depSum + (dep.amountUsed || 0), 0);
    }
    return paymentSum;
  }, 0);

  const balanceDue = (total || 0) - totalPayments;

  console.log("Invoice Total:", total);
  console.log("Total Payments (excluding converted):", totalPayments);
  console.log("Balance Due:", balanceDue);
  console.log("Converted payments should be excluded from totalPayments calculation");

  let formattedDate = "";
  let formattedTime = "";

  if (invoiceData.updatedAt) {
    formattedDate = new Date(invoiceData.updatedAt).toLocaleDateString(
      "en-US",
      {
        month: "long",
        day: "numeric",
        year: "numeric",
      }
    );

    formattedTime = new Date(invoiceData.updatedAt).toLocaleTimeString(
      "en-US",
      {
        timeStyle: "long",
      }
    );
  }

  console.log("Payment Info", paymentInfo);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header with logo */}
        <View style={styles.header}>
          {logo ? (
            <Image style={styles.logo} src={logo} />
          ) : (
            <Text style={styles.text}>Logo Missing</Text>
          )}
          <View style={{ alignItems: "flex-end" }}>
            <Text style={{ fontSize: 12, fontWeight: "bold" }}>
              {clinic.name}
            </Text>
            <Text style={styles.text}>{clinic.address?.street || ""}</Text>
            <Text style={styles.text}>
              {`${clinic.address?.city || ""}, ${
                clinic.address?.state || ""
              }, ${clinic.address?.zipCode || ""}`}
            </Text>
            <Text style={styles.text}>{clinic.phoneNumber || ""}</Text>
          </View>
        </View>

        {/* Clinician and Invoice info */}
        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-evenly",
            width: "100%",
            marginVertical: 10,
            paddingBottom: 20,
          }}>
          <View style={{ flexBasis: "70%" }}>
            <Text style={styles.text}>
              {clinician.firstName} {clinician.lastName}
            </Text>
            <Text style={styles.text}>{clinician.invoiceDescriptor}</Text>
          </View>

          <View style={{ flexBasis: "30%", alignItems: "flex-end" }}>
            <Text style={styles.text}>Invoice: {invoiceData.invoiceId}</Text>
            {invoiceData.status === 'refunded' && (
              <Text style={{ ...styles.text, color: "red", fontWeight: "bold", fontSize: 14 }}>
                *** REFUNDED ***
              </Text>
            )}
            {invoiceData.status === 'void' && (
              <Text style={{ ...styles.text, color: "red", fontWeight: "bold", fontSize: 18 }}>
                *** VOIDED ***
              </Text>
            )}
            <Text style={styles.text}>
              Service Date:{" "}
              {new Date(invoiceData.serviceDate).toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </Text>
          </View>
        </View>

        {/* Client and Bill To */}
        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-evenly",
            width: "100%",
          }}>
          {/* Client */}
          <View
            style={{
              marginBottom: 20,
              borderWidth: 1,
              flex: 1,
              marginRight: 10,
            }}>
            <View
              style={{
                borderBottom: 1,
                paddingHorizontal: 10,
                paddingVertical: 6,
              }}>
              <Text style={styles.text}>Client</Text>
            </View>
            <View
              style={{
                paddingVertical: 8,
                paddingHorizontal: 10,
              }}>
              <Text style={styles.text}>{`${(client.firstName || "").trim()} ${(
                client.lastName || ""
              ).trim()}`}</Text>
              <Text style={styles.text}>
                {address.aptUnit ? `${address.aptUnit} - ` : ""}
                {`${address.streetNumber || ""} ${address.streetName || ""}`}
              </Text>

              <Text style={styles.text}>{`${address.city || ""}, ${
                address.state || ""
              }, ${address.zipCode || ""}`}</Text>
            </View>
          </View>

          {/* Bill To */}
          <View
            style={{
              marginBottom: 20,
              borderWidth: 1,
              flex: 1,
              marginLeft: 10,
            }}>
            <View
              style={{
                borderBottom: 1,
                paddingHorizontal: 10,
                paddingVertical: 6,
              }}>
              <Text style={styles.text}>Bill To</Text>
            </View>
            <View
              style={{
                paddingVertical: 8,
                paddingHorizontal: 10,
              }}>
              {billToType === "insurer" ? (
                <>
                  <Text style={styles.text}>{insurer.name || ""}</Text>
                  <Text style={styles.text}>
                    {`${
                      insurer.address?.aptNumber
                        ? insurer.address.aptNumber + " - "
                        : ""
                    }${insurer.address?.street || ""}`}
                  </Text>
                  <Text style={styles.text}>{`${insurer.address?.city || ""}, ${
                    insurer.address?.state || ""
                  }, ${insurer.address?.zipCode || ""}`}</Text>
                </>
              ) : (
                <>
                  <Text style={styles.text}>{`${(parentInfo._id
                    ? parentInfo.firstName
                    : client.firstName || ""
                  ).trim()} ${(parentInfo._id
                    ? parentInfo.lastName
                    : client.lastName || ""
                  ).trim()}`}</Text>
                  <Text style={styles.text}>
                    {`${
                      parentInfo._id
                        ? parentInfo.address?.aptUnit
                          ? `${parentInfo.address.aptUnit} - `
                          : ""
                        : client.address?.aptUnit
                        ? `${client.address.aptUnit} - `
                        : ""
                    }${
                      parentInfo._id
                        ? parentInfo.address?.streetNumber || ""
                        : client.address?.streetNumber || ""
                    } ${
                      parentInfo._id
                        ? parentInfo.address?.streetName || ""
                        : client.address?.streetName || ""
                    }`}
                  </Text>

                  <Text style={styles.text}>{`${
                    parentInfo._id
                      ? parentInfo.address?.city || ""
                      : client.address?.city || ""
                  }, ${
                    parentInfo._id
                      ? parentInfo.address?.state || ""
                      : client.address?.state || ""
                  }, ${
                    parentInfo._id
                      ? parentInfo.address?.zipCode || ""
                      : client.address?.zipCode || ""
                  }`}</Text>
                </>
              )}
            </View>
          </View>
        </View>

        {/* Charges Table */}
        <View style={styles.table}>
          <View style={styles.tableRow}>
            <View style={{ ...styles.tableColHeader, width: "50%" }}>
              <Text style={styles.tableCell}>Description</Text>
            </View>
            <View style={{ ...styles.tableColHeader, width: "5%" }}>
              <Text style={styles.tableCell}>Qty</Text>
            </View>
            <View style={{ ...styles.tableColHeader, width: "30%" }}>
              <Text style={styles.tableCell}>Notes</Text>
            </View>
            <View style={{ ...styles.tableColHeader, width: "15%" }}>
              <Text style={styles.tableCell}>Amount</Text>
            </View>
          </View>

          {/* Services Rows */}
          {services.map((service, index) => {
            if (!isRefunded) {
              // Normal invoice - show services normally
              return (
                <View key={index} style={styles.tableRow}>
                  <View style={{ ...styles.tableCol, width: "50%" }}>
                    <Text style={styles.tableCell}>{service.description}</Text>
                  </View>
                  <View style={{ ...styles.tableCol, width: "5%", alignItems: "flex-end" }}>
                    <Text style={styles.tableCell}>{service.quantity}</Text>
                  </View>
                  <View style={{ ...styles.tableCol, width: "30%" }}>
                    <Text style={styles.tableCell}>{service.notes}</Text>
                  </View>
                  <View style={{ ...styles.tableCol, width: "15%", alignItems: "flex-end" }}>
                    <Text style={styles.tableCell}>${(service.cost * service.quantity)?.toFixed(2)}</Text>
                  </View>
                </View>
              );
            } else {
              // Refunded invoice - show BOTH original service AND refund offset
              return (
                <React.Fragment key={index}>
                  {/* Original Service Line */}
                  <View style={styles.tableRow}>
                    <View style={{ ...styles.tableCol, width: "50%" }}>
                      <Text style={styles.tableCell}>{service.description}</Text>
                    </View>
                    <View style={{ ...styles.tableCol, width: "5%", alignItems: "flex-end" }}>
                      <Text style={styles.tableCell}>{service.quantity}</Text>
                    </View>
                    <View style={{ ...styles.tableCol, width: "30%" }}>
                      <Text style={styles.tableCell}>{service.notes}</Text>
                    </View>
                    <View style={{ ...styles.tableCol, width: "15%", alignItems: "flex-end" }}>
                      <Text style={styles.tableCell}>${(service.cost * service.quantity)?.toFixed(2)}</Text>
                    </View>
                  </View>
                  
                  {/* Refund Offset Line */}
                  <View style={styles.tableRow}>
                    <View style={{ ...styles.tableCol, width: "50%" }}>
                      <Text style={{ ...styles.tableCell, color: "red" }}>
                        REFUND: {service.description}
                      </Text>
                    </View>
                    <View style={{ ...styles.tableCol, width: "5%", alignItems: "flex-end" }}>
                      <Text style={{ ...styles.tableCell, color: "red" }}>
                        -{service.quantity}
                      </Text>
                    </View>
                    <View style={{ ...styles.tableCol, width: "30%" }}>
                      <Text style={{ ...styles.tableCell, color: "red" }}>
                        {service.notes ? `Refund of: ${service.notes}` : "Refund"}
                      </Text>
                    </View>
                    <View style={{ ...styles.tableCol, width: "15%", alignItems: "flex-end" }}>
                      <Text style={{ ...styles.tableCell, color: "red" }}>
                        -${(service.cost * service.quantity)?.toFixed(2)}
                      </Text>
                    </View>
                  </View>
                </React.Fragment>
              );
            }
          })}

          {/* Subtotal Row */}
          <View style={styles.tableRow}>
            <View
              style={{
                ...styles.tableCol,
                width: "85%",
                borderRightWidth: 1,
                alignItems: "flex-end",
              }}>
              <Text style={styles.tableCell}>
                {isRefunded ? "Net Subtotal (After Refund)" : "Subtotal"}
              </Text>
            </View>
            <View
              style={{
                ...styles.tableCol,
                width: "15%",
                alignItems: "flex-end",
              }}>
              <Text style={styles.tableCell}>
                ${subtotal?.toFixed(2)}
              </Text>
            </View>
          </View>

          {/* Taxes Row */}
          <View style={styles.tableRow}>
            <View
              style={{
                ...styles.tableCol,
                width: "85%",
                borderRightWidth: 1,
                alignItems: "flex-end",
              }}>
              <Text style={styles.tableCell}>
                {isRefunded ? "Net Tax (After Refund)" : "Tax"}
              </Text>
            </View>
            <View
              style={{
                ...styles.tableCol,
                width: "15%",
                alignItems: "flex-end",
              }}>
              <Text style={styles.tableCell}>
                ${tax?.toFixed(2)}
              </Text>
            </View>
          </View>

          {/* Total Row */}
          <View style={styles.tableRow}>
            <View
              style={{
                ...styles.tableCol,
                width: "85%",
                borderRightWidth: 1,
                alignItems: "flex-end",
              }}>
              <Text style={{ ...styles.tableCell, fontWeight: "bold" }}>
                {isRefunded ? "Net Total (After Refund)" : "Total"}
              </Text>
            </View>
            <View
              style={{
                ...styles.tableCol,
                width: "15%",
                alignItems: "flex-end",
              }}>
              <Text style={{ ...styles.tableCell, fontWeight: "bold" }}>
                ${total?.toFixed(2)}
              </Text>
            </View>
          </View>

          {/* Payments Section */}
          {Array.isArray(paymentInfo) && paymentInfo.length > 0 && (
            <>
              {paymentInfo.map((paymentItem, index) => {
                const payment = paymentItem.payment || paymentItem;
                if (!payment) return null;

                // Check if this payment belongs to this specific invoice (handle split invoices)
                const isForThisInvoice = (() => {
                  // For direct payment.invoiceId match
                  if (payment.invoiceId?.invoiceId === invoiceData.invoiceId) {
                    return true;
                  }
                  
                  // For array of invoices, check exact match (important for split invoices)
                  if (Array.isArray(payment.invoiceId)) {
                    return payment.invoiceId.some(inv => inv.invoiceId === invoiceData.invoiceId);
                  }
                  
                  // For split invoices, ensure we only show payments specifically allocated to this part
                  // Check if payment amount was specifically allocated to this invoice part
                  if (payment.invoiceAllocations && Array.isArray(payment.invoiceAllocations)) {
                    return payment.invoiceAllocations.some(allocation => 
                      allocation.invoiceId === invoiceData.invoiceId
                    );
                  }
                  
                  return false;
                })();

                if (!isForThisInvoice) return null;

                return (
                  <React.Fragment key={index}>
                    {/* Show payments */}
                    {!payment.deposit && payment.amount !== 0 && (
                      <React.Fragment>
                        {(payment.isRefund && payment.refundedAt) || payment.convertedToDeposit ? (
                          // Show refund lines if payment was actually refunded OR converted to deposit
                          <React.Fragment>
                            {/* Original Payment Line */}
                            <View style={styles.tableRow}>
                              <View
                                style={{
                                  ...styles.tableCol,
                                  width: "85%",
                                  borderRightWidth: 1,
                                  alignItems: "flex-end",
                                }}>
                                <Text style={styles.tableCell}>
                                  {`Payment $${Math.abs(payment.amount || 0).toFixed(2)} (${
                                    payment.paymentMethod
                                      ? (clinic.paymentMethods || []).find(
                                          (method) => method.value === payment.paymentMethod
                                        )?.englishDescription ||
                                        payment.paymentMethod
                                      : "Unknown Method"
                                  }) - ${new Date(
                                    payment.paymentDate
                                  ).toLocaleDateString("en-US", {
                                    year: "numeric",
                                    month: "short",
                                    day: "numeric",
                                    timeZone: "UTC",
                                  })}`}
                                </Text>
                              </View>
                              <View
                                style={{
                                  ...styles.tableCol,
                                  width: "15%",
                                  alignItems: "flex-end",
                                }}>
                                <Text style={styles.tableCell}>
                                  {`-$${Math.abs(payment.amount || 0).toFixed(2)}`}
                                </Text>
                              </View>
                            </View>
                            
                            {/* Refund/Conversion Line */}
                            <View style={styles.tableRow}>
                              <View
                                style={{
                                  ...styles.tableCol,
                                  width: "85%",
                                  borderRightWidth: 1,
                                  alignItems: "flex-end",
                                }}>
                                <Text style={{ ...styles.tableCell, color: "red" }}>
                                  {payment.convertedToDeposit 
                                    ? `Converted to Deposit $${Math.abs(payment.amount || 0).toFixed(2)} (${
                                        payment.paymentMethod
                                          ? (clinic.paymentMethods || []).find(
                                              (method) => method.value === payment.paymentMethod
                                            )?.englishDescription ||
                                            payment.paymentMethod
                                          : "Unknown Method"
                                      }) - ${new Date(
                                        payment.paymentDate
                                      ).toLocaleDateString("en-US", {
                                        year: "numeric",
                                        month: "short",
                                        day: "numeric",
                                        timeZone: "UTC",
                                      })}`
                                    : `Refund $${Math.abs(payment.amount || 0).toFixed(2)} (${
                                        payment.paymentMethod
                                          ? (clinic.paymentMethods || []).find(
                                              (method) => method.value === payment.paymentMethod
                                            )?.englishDescription ||
                                            payment.paymentMethod
                                          : "Unknown Method"
                                      }) - ${new Date(
                                        payment.refundedAt || payment.paymentDate
                                      ).toLocaleDateString("en-US", {
                                        year: "numeric",
                                        month: "short",
                                        day: "numeric",
                                        timeZone: "UTC",
                                      })}`
                                  }
                                </Text>
                              </View>
                              <View
                                style={{
                                  ...styles.tableCol,
                                  width: "15%",
                                  alignItems: "flex-end",
                                }}>
                                <Text style={{ ...styles.tableCell, color: "red" }}>
                                  {`+$${Math.abs(payment.amount || 0).toFixed(2)}`}
                                </Text>
                              </View>
                            </View>
                          </React.Fragment>
                        ) : (
                          // For payments that haven't been refunded (including payments for refunded invoices)
                          <View style={styles.tableRow}>
                            <View
                              style={{
                                ...styles.tableCol,
                                width: "85%",
                                borderRightWidth: 1,
                                alignItems: "flex-end",
                              }}>
                              <Text style={styles.tableCell}>
                                {`Payment $${Math.abs(payment.amount || 0).toFixed(2)} (${
                                  payment.paymentMethod
                                    ? (clinic.paymentMethods || []).find(
                                        (method) => method.value === payment.paymentMethod
                                      )?.englishDescription ||
                                      payment.paymentMethod
                                    : "Unknown Method"
                                }) - ${new Date(
                                  payment.paymentDate
                                ).toLocaleDateString("en-US", {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                  timeZone: "UTC",
                                })}`}
                              </Text>
                            </View>
                            <View
                              style={{
                                ...styles.tableCol,
                                width: "15%",
                                alignItems: "flex-end",
                              }}>
                              <Text style={styles.tableCell}>
                                {`-$${Math.abs(payment.amount || 0).toFixed(2)}`}
                              </Text>
                            </View>
                          </View>
                        )}
                      </React.Fragment>
                    )}
                    {/* Only show deposit usage lines for this invoice */}
                    {payment.deposit && Array.isArray(payment.depositUsage) &&
                      payment.depositUsage
                        .filter(dep => dep.invoiceId === invoiceData._id || dep.invoiceNumber === invoiceData.invoiceId)
                        .map((deposit, dIndex) => (
                          <View
                            style={styles.tableRow}
                            key={`${index}-${dIndex}`}>
                            <View
                              style={{
                                ...styles.tableCol,
                                width: "85%",
                                borderRightWidth: 1,
                                alignItems: "flex-end",
                              }}>
                              <Text style={styles.tableCell}>
                                {`Deposit Applied $${(deposit.amountUsed || 0).toFixed(2)} (${
                                  payment.paymentMethod
                                    ? (clinic.paymentMethods || []).find(
                                        (method) => method.value === payment.paymentMethod
                                      )?.englishDescription ||
                                      payment.paymentMethod
                                    : "Unknown Method"
                                }) - ${new Date(
                                  deposit.dateUsed
                                ).toLocaleDateString("en-US", {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                  timeZone: "UTC",
                                })}`}
                              </Text>
                            </View>
                            <View
                              style={{
                                ...styles.tableCol,
                                width: "15%",
                                alignItems: "flex-end",
                              }}>
                              <Text style={styles.tableCell}>
                                -${(deposit.amountUsed || 0).toFixed(2)}
                              </Text>
                            </View>
                          </View>
                        ))}
                  </React.Fragment>
                );
              })}
            </>
          )}

          {/* Balance Due Row */}
          <View style={styles.tableRow}>
            <View
              style={{
                ...styles.tableCol,
                width: "85%",
                borderRightWidth: 1,
                alignItems: "flex-end",
              }}>
              <Text style={{ ...styles.tableCell, fontWeight: "bold" }}>
                {balanceDue < 0 ? "Credit Balance" : "Balance Due"}
              </Text>
            </View>
            <View
              style={{
                ...styles.tableCol,
                width: "15%",
                alignItems: "flex-end",
              }}>
              <Text style={{ ...styles.tableCell, fontWeight: "bold" }}>
                ${Math.abs(balanceDue)?.toFixed(2)}
              </Text>
            </View>
          </View>
        </View>


        {/* Notes Section */}
        <View
          style={{
            marginTop: 20,
          }}>
          <Text style={styles.text}>Notes</Text>
          <View
            style={{
              marginTop: 5,
              padding: 5,
              borderWidth: 1,
              width: "100%",
              minHeight: 60,
            }}>
            <Text style={styles.text}>
              {isRefunded && invoiceData.notes 
                ? `ORIGINAL NOTES: ${invoiceData.notes}` 
                : invoiceData.notes}
            </Text>
          </View>
        </View>

        {/* Signature */}
        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            width: "100%",
            marginTop: 50,
          }}>
          {" "}
          <Text style={styles.text}>
            Signature: ________________________________
          </Text>
          <Text style={styles.text}>
            Date: ________________________________
          </Text>
        </View>

        <View
          style={{
            flex: 1,
            justifyContent: "flex-end",
            alignItems: "center",
          }}>
          <Text style={{ fontSize: 8 }}>
            [Version {invoiceData.__v + 1}. Last updated on {formattedDate}{" "}
            {formattedTime}]
          </Text>
        </View>
      </Page>
    </Document>
  );
};

// Styles for the PDF document
const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    padding: 30,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 20,
  },
  logo: {
    maxWidth: 180,
    maxHeight: 100,
    width: "auto",
    height: "auto",
  },

  borderSection: {
    marginBottom: 20,
    borderWidth: 1,
  },
  title: {
    fontSize: 18,
    marginBottom: 10,
  },
  text: {
    fontSize: 12,
  },
  table: {
    display: "table",
    width: "auto",
    borderStyle: "solid",
    borderWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 0,
  },
  tableRow: {
    flexDirection: "row",
  },
  tableColHeader: {
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    backgroundColor: "#D3D3D3",
  },
  tableCol: {
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
  },
  tableCell: {
    margin: 5,
    fontSize: 10,
  },
});
